import { useState, type ReactNode } from 'react';
import { useClient } from './client';
import { useFan } from './fan';
import { navigate, useRoute, type Route } from './router';
import { Onboarding } from './screens/Onboarding';
import { Home } from './screens/Home';
import { Room } from './screens/Room';
import { ReceiptScreen } from './screens/ReceiptScreen';
import { ControlRoom } from './control/ControlRoom';
import { Library } from './screens/Library';
import { Moderation } from './screens/Moderation';
import { CatchUp } from './screens/CatchUp';
import { PublicStory } from './screens/PublicStory';
import { FirstRun } from './screens/FirstRun';
import { normalizeLanguage } from './director/i18n';
import type { DirectorLanguage } from '@clutch/core';

export function App() {
  const route = useRoute();
  if (route.name === 'control') return <ControlRoom roomId={route.roomId} layout="page" />;
  if (route.name === 'moderation') return <Moderation />;
  // Public: no fan needed (a fresh incognito browser opens a shared story).
  if (route.name === 'story') return <div className="app app--story"><PublicStory key={route.storyId} storyId={route.storyId} /></div>;
  return <div className={`app app--${route.name}`}><PhoneRoutes route={route} /></div>;
}

function PhoneRoutes({ route }: { route: Route }): ReactNode {
  const client = useClient();
  const [fan, setFan, ready] = useFan(client);
  const [editing, setEditing] = useState(false);
  const [setup, setSetup] = useState(false);
  const [setupLanguage, setSetupLanguage] = useState<DirectorLanguage>();
  if (!ready) return <main className="empty-state" role="status">CLUTCH…</main>;
  // First run on the home route: basketball and a story first, the team setup one tap away.
  if (!fan && !setup && route.name === 'home' && client.director) return <FirstRun onStart={setFan} onSetup={(language) => { setSetupLanguage(language); setSetup(true); }} />;
  if (!fan || editing)
    return (
      <Onboarding
        initialLanguage={setupLanguage}
        initial={editing ? fan : undefined}
        onCancel={() => setEditing(false)}
        onDone={(f) => {
          setFan(f);
          setEditing(false);
        }}
      />
    );
  switch (route.name) {
    case 'takes':
      return <Library fan={fan} />;
    case 'room':
      return <Room key={route.roomId} roomId={route.roomId} fan={fan} />;
    case 'catchup':
      return <CatchUp key={route.roomId} roomId={route.roomId} fan={fan} {...langParam()} />;
    case 'receipt':
      return <ReceiptScreen roomId={route.roomId} fan={fan} />;
    default:
      return (
        <Home
          fan={fan}
          onEdit={() => setEditing(true)}
          onJoin={(roomId) => navigate(`/room/${encodeURIComponent(roomId)}`)}
        />
      );
  }
}

function langParam(): { initialLanguage?: DirectorLanguage } {
  const l = normalizeLanguage(new URLSearchParams(location.search).get('lang'));
  return l ? { initialLanguage: l } : {};
}
